import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { BoxHealth, RunnerRow } from '../lib/fleet';
import { ErrorState } from './ErrorState';
import { Skeleton } from './Skeleton';
import { StatusPill, type StatusTone } from './StatusPill';

// "WAITING FOR HANDSHAKE" on the box step (canvas ONBOARDING artboard). Presenter;
// `<OnboardingBoxStep/>` owns the roster poll.
//
// EVERY ✓ IS A FIELD THE ROSTER SERVED, never an inference. The four facts are
// the ones `GET /v1/runners` actually carries for a box:
//
//   enrolled        `enrolled_iso` set
//   first heartbeat `last_heartbeat_iso` set — NOT `status === 'online'`: the
//                   enroll flips a row `pending → online` before the box has
//                   ever dialed in (`RunnerWireSchema.health`), so status would
//                   tick a machine that never beat
//   runner version  `runner_version` reported
//   hardware        `specs` reported (12 of 27 prod rows carry none)
//
// A missing fact is "…", never a ✓ and never a guess. The canvas's "outbound to
// mcp" and "docker · git" lines are cut: no field reports either. The panel
// reads WAITING, with the live dot, until some box has beaten — the same fact
// the API's ladder settles `box_attached` on, so the step moves on its own a
// poll later. Health rides each box's name as the roster's own word (#53).
//
// A FAILED roster read is not an empty fleet (the `<DonateBoxStep/>` rule):
// "waiting for a server" over a 500 would send a maintainer off to enrol a
// duplicate, so the failure renders the retryable error state instead.

const HEALTH: Readonly<Record<BoxHealth, { readonly key: string; readonly tone: StatusTone }>> = {
  online: { key: 'onboarding.box_ready', tone: 'live' },
  awaiting_first_beat: { key: 'onboarding.box_starting', tone: 'neutral' },
  never_beat: { key: 'onboarding.box_attention', tone: 'danger' },
  stale: { key: 'onboarding.box_attention', tone: 'danger' },
  // `useRunners` drops revoked rows, so this arm is for the type, not the screen.
  revoked: { key: 'onboarding.box_attention', tone: 'danger' },
};

export type OnboardingHandshakeProps = {
  /** `undefined` while in flight OR after a refusal — never `?? []`. */
  readonly runners: readonly RunnerRow[] | undefined;
  readonly failed: boolean;
  readonly error: unknown;
  readonly onRetry: () => void;
};

type Fact = { readonly done: boolean; readonly text: string };

const GIB = 1024 ** 3;

/** The four roster facts for one box, in handshake order. */
export function handshakeFacts(runner: RunnerRow): readonly Fact[] {
  const specs = runner.specs;
  return [
    runner.enrolled_iso !== null
      ? { done: true, text: t('onboarding.handshake_enrolled') }
      : { done: false, text: t('onboarding.handshake_enrolled_pending') },
    runner.last_heartbeat_iso !== null
      ? { done: true, text: t('onboarding.handshake_heartbeat') }
      : { done: false, text: t('onboarding.handshake_heartbeat_pending') },
    runner.runner_version !== null
      ? { done: true, text: t('onboarding.handshake_version', { version: runner.runner_version }) }
      : { done: false, text: t('onboarding.handshake_version_pending') },
    specs !== null
      ? {
          done: true,
          text: t('onboarding.handshake_specs', {
            arch: specs.arch,
            cores: specs.cpuCores,
            mem: Math.round(specs.memBytes / GIB),
          }),
        }
      : { done: false, text: t('onboarding.handshake_specs_pending') },
  ];
}

export function OnboardingHandshake(props: OnboardingHandshakeProps): JSX.Element {
  /** Newest first: the box the maintainer just ran the command on leads. */
  const boxes = (): readonly RunnerRow[] =>
    [...(props.runners ?? [])].sort((a, b) => b.created_iso.localeCompare(a.created_iso));
  const beaten = (): boolean => boxes().some((r) => r.last_heartbeat_iso !== null);
  const announcement = (): string => {
    const facts = boxes().flatMap((r) => handshakeFacts(r));
    return t('onboarding.handshake_announce', {
      state: t(beaten() ? 'onboarding.handshake_complete' : 'onboarding.handshake_waiting'),
      done: facts.filter((f) => f.done).length,
      total: facts.length,
    });
  };

  return (
    <section
      class="onboarding-handshake"
      classList={{ 'onboarding-handshake--live': !beaten() && !props.failed }}
      aria-labelledby="onboarding-handshake-label"
    >
      <div class="onboarding-handshake__head">
        <h3 id="onboarding-handshake-label" class="onboarding-handshake__label">
          {t(beaten() ? 'onboarding.handshake_complete' : 'onboarding.handshake_waiting')}
        </h3>
        <Show when={!beaten() && !props.failed}>
          <span class="status__dot" aria-hidden="true" />
        </Show>
      </div>
      {/* The WAITING → COMPLETE flip and each newly reported fact change this
          line, so a screen reader hears the box come up without re-reading
          the list. Empty until the roster has landed. */}
      <p class="sr-only" role="status">
        {props.runners === undefined ? '' : announcement()}
      </p>
      <Show when={props.failed}>
        <ErrorState error={props.error} onRetry={props.onRetry} />
      </Show>
      <Show when={!props.failed && props.runners === undefined}>
        <Skeleton height="4.5rem" />
      </Show>
      <Show when={props.runners !== undefined && boxes().length === 0}>
        <ul class="onboarding-handshake__facts">
          <li class="onboarding-handshake__fact" data-done="false">
            <span class="onboarding-handshake__glyph" aria-hidden="true">
              …
            </span>
            {t('onboarding.handshake_no_box')}
          </li>
        </ul>
      </Show>
      <For each={boxes()}>
        {(runner) => (
          <div class="onboarding-handshake__box" data-health={runner.health}>
            <div class="onboarding-handshake__box-head">
              <span class="onboarding-handshake__box-name">
                {runner.hostname ?? runner.display_name}
              </span>
              <StatusPill tone={HEALTH[runner.health].tone}>
                {t(HEALTH[runner.health].key)}
              </StatusPill>
            </div>
            <ul class="onboarding-handshake__facts">
              <For each={handshakeFacts(runner)}>
                {(fact) => (
                  <li class="onboarding-handshake__fact" data-done={String(fact.done)}>
                    <span class="onboarding-handshake__glyph" aria-hidden="true">
                      {fact.done ? '✓' : '…'}
                    </span>
                    <span class="sr-only">
                      {t(
                        fact.done
                          ? 'onboarding.step_state_done'
                          : 'onboarding.handshake_fact_pending',
                      )}
                    </span>
                    {fact.text}
                  </li>
                )}
              </For>
            </ul>
          </div>
        )}
      </For>
    </section>
  );
}
